import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";

export interface TableColumn<T = any> {
  id: string;
  label: string;
  accessor: string | ((item: T) => any);
  width?: string;
  sortable?: boolean;
  searchable?: boolean; // New property for search functionality
  filterable?: boolean; // New property for filter functionality
  filterType?: "select" | "text"; // Auto-detected if not specified
  filterOptions?: string[]; // External filter options (overrides auto-generated)
  alwaysVisible?: boolean; // New property for always visible columns
  defaultVisible?: boolean; // New property for default visibility (defaults to true)
  sortValue?: (item: T) => any; // Custom value for sorting (e.g., numeric conversion for age)
  filterValue?: (item: T) => any; // Custom value for filtering (e.g., computed status for display)
  render?: (value: any, item: T) => React.ReactNode;
}

interface PaginationConfig {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

interface CustomizableTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  emptyMessage?: string;
  pagination?: PaginationConfig; // Optional pagination configuration
  searchMode?: 'client' | 'server'; // Search mode: client (Fuse.js) or server (API)
  onSearchChange?: (query: string) => void; // Callback when search query changes (for server mode)
  searchDebounceMs?: number; // Debounce delay for search (default: 300ms)
}

export default function CustomizableTable<T>({
  data,
  columns,
  loading = false,
  emptyMessage,
  pagination,
  searchMode = 'client',
  onSearchChange,
  searchDebounceMs = 300,
}: CustomizableTableProps<T>) {
  const { t } = useTranslation("table");
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns.filter((col) => col.defaultVisible !== false).map((col) => col.id),
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [openFilterColumnId, setOpenFilterColumnId] = useState<string | null>(
    null,
  );
  const [filterSearchQueries, setFilterSearchQueries] = useState<
    Record<string, string>
  >({});
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Store latest onSearchChange callback in a ref to avoid re-triggering effect
  const onSearchChangeRef = useRef(onSearchChange);
  const isFirstRender = useRef(true);
  const lastSearchQuery = useRef(searchQuery);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Reset visibleColumns when columns prop changes
  useEffect(() => {
    setVisibleColumns(columns.filter((col) => col.defaultVisible !== false).map((col) => col.id));
  }, [columns]);

  // Handle server-side search with debounce
  useEffect(() => {
    // Skip initial render to avoid unnecessary API call on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSearchQuery.current = searchQuery;
      return;
    }

    // Only trigger if searchQuery actually changed
    if (searchMode === 'server' && onSearchChangeRef.current && lastSearchQuery.current !== searchQuery) {
      lastSearchQuery.current = searchQuery;

      const timer = setTimeout(() => {
        onSearchChangeRef.current?.(searchQuery);
      }, searchDebounceMs);

      return () => clearTimeout(timer);
    }
  }, [searchQuery, searchMode, searchDebounceMs]);

  const handleColumnToggle = (columnId: string) => {
    // Prevent toggling always visible columns
    const column = columns.find((col) => col.id === columnId);
    if (column?.alwaysVisible) return;

    setVisibleColumns((prev) => {
      const newColumns = prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId];

      // Ensure at least one column is visible
      return newColumns.length > 0 ? newColumns : prev;
    });
  };

  const handleSort = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (!column?.sortable) return;

    setSortConfig((prevConfig) => {
      if (prevConfig?.key === columnId) {
        // Same column: cycle through asc -> desc -> null
        if (prevConfig.direction === "asc") {
          return { key: columnId, direction: "desc" };
        } else if (prevConfig.direction === "desc") {
          return null; // Clear sorting
        }
      }
      // New column or no previous config: start with asc
      return { key: columnId, direction: "asc" };
    });
  };

  const handleColumnFilter = (
    columnId: string,
    value: string,
    checked: boolean,
  ) => {
    setColumnFilters((prev) => {
      const currentValues = prev[columnId] || [];
      if (checked) {
        // Add value if not already present
        if (!currentValues.includes(value)) {
          return { ...prev, [columnId]: [...currentValues, value] };
        }
      } else {
        // Remove value
        const newValues = currentValues.filter((v) => v !== value);
        if (newValues.length === 0) {
          const { [columnId]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [columnId]: newValues };
      }
      return prev;
    });
  };

  const clearColumnFilter = (columnId: string) => {
    setColumnFilters((prev) => {
      const { [columnId]: removed, ...rest } = prev;
      return rest;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchQuery("");
    setOpenFilterColumnId(null);
    setFilterSearchQueries({});
  };

  const toggleFilterDropdown = (columnId: string) => {
    setOpenFilterColumnId(openFilterColumnId === columnId ? null : columnId);
  };

  const updateFilterSearchQuery = (columnId: string, query: string) => {
    setFilterSearchQueries((prev) => ({
      ...prev,
      [columnId]: query,
    }));
  };

  const getFilteredOptions = (columnId: string, allOptions: string[]) => {
    const searchQuery = filterSearchQueries[columnId];
    if (!searchQuery?.trim()) {
      return allOptions;
    }

    const query = searchQuery.toLowerCase();
    return allOptions.filter((option) => option.toLowerCase().includes(query));
  };

  const getValueFromItem = (
    item: T,
    accessor: string | ((item: T) => any),
  ): any => {
    if (typeof accessor === "function") {
      return accessor(item);
    }
    return accessor
      .split(".")
      .reduce((obj: any, key: string) => obj?.[key], item);
  };

  // Create Fuse.js instance for fuzzy search
  const fuse = useMemo(() => {
    const searchableColumns = columns.filter((col) => col.searchable !== false);
    const fuseOptions = {
      keys: searchableColumns.map((col) => ({
        name: col.id,
        weight: 1,
        getFn: (item: T) => {
          const value = getValueFromItem(item, col.accessor);
          if (Array.isArray(value)) {
            return value.join(" ");
          }
          return String(value || "");
        },
      })),
      threshold: 0.3, // Lower = more strict, higher = more fuzzy
      distance: 100,
      includeScore: true,
      minMatchCharLength: 1,
    };
    return new Fuse(data, fuseOptions);
  }, [data, columns]);

  // Filter data based on search query
  const searchFilteredData = useMemo(() => {
    // Server mode: data is already filtered by server, use it directly
    if (searchMode === 'server') {
      return data;
    }

    // Client mode: use Fuse.js for local filtering
    if (!searchQuery.trim()) {
      return data;
    }
    const results = fuse.search(searchQuery.trim());
    return results.map((result) => result.item);
  }, [data, searchQuery, fuse, searchMode]);

  // Generate filter options for each filterable column
  const filterOptions = useMemo(() => {
    const options: Record<string, string[]> = {};

    columns.forEach((column) => {
      if (!column.filterable || !visibleColumns.includes(column.id)) return;

      // Use external filter options if provided
      if (column.filterOptions && column.filterOptions.length > 0) {
        options[column.id] = column.filterOptions.sort();
        return;
      }

      // Otherwise auto-detect from data
      const uniqueValues = Array.from(
        new Set(
          data
            .map((item) => {
              // Use filterValue function if provided, otherwise use accessor
              const value = column.filterValue
                ? column.filterValue(item)
                : getValueFromItem(item, column.accessor);
              return String(value || "").trim();
            })
            .filter(Boolean),
        ),
      ).sort();

      // If less than 15 unique values, use select dropdown
      if (uniqueValues.length <= 15 && uniqueValues.length > 1) {
        options[column.id] = uniqueValues;
      }
    });

    return options;
  }, [data, columns, visibleColumns]);

  // Filter by column filters (multi-select)
  const filteredData = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([_, values]) => values.length > 0,
    );

    if (activeFilters.length === 0) {
      return searchFilteredData;
    }

    return searchFilteredData.filter((item) => {
      return activeFilters.every(([columnId, filterValues]) => {
        const column = columns.find((col) => col.id === columnId);
        if (!column) return true;

        // Use filterValue function if provided, otherwise use accessor
        const cellValue = column.filterValue
          ? column.filterValue(item)
          : getValueFromItem(item, column.accessor);
        const cellString = String(cellValue || "");

        // Check if cell value matches any of the selected filter values
        return filterValues.includes(cellString);
      });
    });
  }, [searchFilteredData, columnFilters, columns]);

  const sortedData = useMemo(() => {
    const dataToSort = [...filteredData];
    if (!sortConfig) return dataToSort;

    const column = columns.find((col) => col.id === sortConfig.key);
    if (!column) return dataToSort;

    return dataToSort.sort((a, b) => {
      // Use sortValue function if provided, otherwise use accessor
      const aValue = column.sortValue
        ? column.sortValue(a)
        : getValueFromItem(a, column.accessor);
      const bValue = column.sortValue
        ? column.sortValue(b)
        : getValueFromItem(b, column.accessor);

      // Handle null/undefined values - put them at the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;

      // Handle numeric comparison for better sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      // Handle string comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  const visibleColumnObjects = columns.filter((col) =>
    visibleColumns.includes(col.id),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span className="ml-2 text-gray-600">{t("loading")}</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Search Bar */}
      <div className="mb-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder={t("searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-12 pr-4 py-3 text-sm border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-primary-400 focus:shadow-md shadow-sm transition-all duration-300 hover:shadow-md"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Clear Filters Button - only show if there are active filters */}
          {Object.keys(columnFilters).length > 0 && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border border-red-200 bg-red-50/80 text-red-600 hover:bg-red-100 transition-all duration-200 hover:shadow-sm"
            >
              <svg
                className="w-3 h-3 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {t("clear")}{" "}
              {Object.values(columnFilters).reduce(
                (total, values) => total + values.length,
                0,
              )}{" "}
              {Object.values(columnFilters).reduce(
                (total, values) => total + values.length,
                0,
              ) > 1
                ? t("filters")
                : t("filter")}
            </button>
          )}
        </div>
      </div>

      {/* Column Selector and Stats */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-700">
            {t("showing")}{" "}
            <span className="text-slate-900 font-semibold">
              {sortedData.length}
            </span>
            {(searchQuery || Object.keys(columnFilters).length > 0) && (
              <span className="text-gray-500">
                {" "}
                {t("of")}{" "}
                <span className="font-semibold">{data.length}</span>
              </span>
            )}{" "}
            {t("items")}
          </div>
          {searchQuery && (
            <div className="flex items-center text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {t("filteredBy")} "{searchQuery}"
            </div>
          )}
          {Object.keys(columnFilters).length > 0 && (
            <div className="flex items-center text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              {Object.values(columnFilters).reduce(
                (total, values) => total + values.length,
                0,
              )}{" "}
              {Object.values(columnFilters).reduce(
                (total, values) => total + values.length,
                0,
              ) > 1
                ? t("filters")
                : t("filter")}{" "}
              {t("filtersActive")}
            </div>
          )}
          {(searchQuery || Object.keys(columnFilters).length > 0) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center text-xs text-red-600 bg-red-50/80 hover:bg-red-100 px-2 py-1 rounded-lg transition-all duration-200 hover:shadow-sm"
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {t("clearAllFilters")}
            </button>
          )}
          {sortConfig && (
            <div className="flex items-center text-xs text-primary-700 bg-primary-50 px-2 py-1 rounded-full">
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
              {t("sortedBy")}{" "}
              {columns.find((col) => col.id === sortConfig.key)?.label} (
              {sortConfig.direction})
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-slate-50 hover:border-gray-400 flex items-center shadow-sm transition-all duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
              />
            </svg>
            {t("customizeColumns")}
          </button>

          {showColumnSelector && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowColumnSelector(false)}
              />
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden">
                <div className="bg-linear-to-r from-slate-50 to-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="text-sm font-semibold text-slate-800">
                    {t("selectColumns")}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {t("chooseColumns")}
                  </div>
                </div>
                <div className="p-3 max-h-64 overflow-y-auto">
                  <div className="space-y-3">
                    {columns.map((column) => {
                      const isChecked = visibleColumns.includes(column.id);
                      const isLastChecked =
                        visibleColumns.length === 1 && isChecked;
                      const isAlwaysVisible = column.alwaysVisible || false;
                      const isDisabled = isLastChecked || isAlwaysVisible;

                      return (
                        <label
                          key={column.id}
                          className={`flex items-center p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => handleColumnToggle(column.id)}
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span
                            className={`ml-3 text-sm ${isDisabled ? "text-gray-400" : "text-gray-700"} font-medium`}
                          >
                            {column.label}
                            {isLastChecked && (
                              <span className="text-xs ml-2 text-amber-600 font-normal">
                                {t("required")}
                              </span>
                            )}
                            {isAlwaysVisible && (
                              <span className="text-xs ml-2 text-primary-600 font-normal">
                                {t("alwaysVisible")}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200 flex flex-col" style={{ minHeight: '500px' }}>
        <div className="flex-1 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-linear-to-r from-primary-600 to-secondary-600 sticky top-0 z-10">
              <tr>
                {visibleColumnObjects.map((column) => (
                  <th
                    key={column.id}
                    style={{ width: column.width, minWidth: "120px" }}
                    className="px-6 py-3 text-left text-sm font-medium text-white/95 tracking-wide whitespace-nowrap border-r border-primary-500/50 last:border-r-0 relative"
                  >
                    <div className="flex items-center justify-between whitespace-nowrap">
                      <div
                        className={`flex items-center ${column.sortable ? "cursor-pointer hover:text-highlight-yellow transition-colors duration-200" : ""}`}
                        onClick={() => column.sortable && handleSort(column.id)}
                      >
                        <span className="truncate">{column.label}</span>
                        {column.sortable && (
                          <svg
                            className="w-4 h-4 ml-2 flex shrink-0 text-white/70"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {sortConfig?.key === column.id ? (
                              sortConfig.direction === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                  className="text-highlight-yellow"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                  className="text-highlight-yellow"
                                />
                              )
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                              />
                            )}
                          </svg>
                        )}
                      </div>

                      {/* Filter Button */}
                      {column.filterable && filterOptions[column.id] && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilterDropdown(column.id);
                            }}
                            className={`relative p-2 rounded-lg transition-all duration-200 ${
                              columnFilters[column.id]?.length > 0
                                ? "bg-highlight-yellow/20 text-highlight-yellow hover:bg-highlight-yellow/30 shadow-lg shadow-highlight-yellow/20"
                                : "text-white/60 hover:text-white hover:bg-white/15"
                            } ${
                              openFilterColumnId === column.id
                                ? "bg-white/20 text-white ring-2 ring-white/30"
                                : ""
                            }`}
                            title={`${t("filterColumn")} ${column.label}`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                              />
                            </svg>
                            {columnFilters[column.id]?.length > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-linear-to-br from-highlight-yellow to-highlight-orange text-gray-800 text-xs rounded-full flex items-center justify-center font-bold shadow-lg shadow-highlight-yellow/50 ring-2 ring-white/50">
                                {columnFilters[column.id].length}
                              </span>
                            )}
                          </button>

                          {/* Filter Dropdown */}
                          {openFilterColumnId === column.id && (
                            <>
                              <div
                                className="fixed inset-0 z-20"
                                onClick={() => setOpenFilterColumnId(null)}
                              />
                              <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200/60 rounded-xl shadow-2xl backdrop-blur-sm z-30 overflow-hidden ring-1 ring-black/5">
                                {/* Header */}
                                <div className="bg-linear-to-r from-primary-50 via-secondary-50 to-primary-100 px-4 py-3 border-b border-primary-200/50">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <svg
                                        className="w-4 h-4 text-primary-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                        />
                                      </svg>
                                      <span className="text-sm font-semibold text-primary-700">
                                        {t("filterColumn")} {column.label}
                                      </span>
                                      {columnFilters[column.id]?.length > 0 && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                                          {columnFilters[column.id].length}{" "}
                                          {t("selected")}
                                        </span>
                                      )}
                                    </div>
                                    {columnFilters[column.id]?.length > 0 && (
                                      <button
                                        onClick={() =>
                                          clearColumnFilter(column.id)
                                        }
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-white hover:bg-red-500 rounded-md transition-all duration-200"
                                      >
                                        <svg
                                          className="w-3 h-3 mr-1"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                        {t("clear")}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                <div className="p-4">
                                  {/* Search within filter options */}
                                  <div className="mb-4">
                                    <div className="relative">
                                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                          className="h-4 w-4 text-gray-400"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                          />
                                        </svg>
                                      </div>
                                      <input
                                        type="text"
                                        placeholder={t("searchOptions")}
                                        value={
                                          filterSearchQueries[column.id] || ""
                                        }
                                        onChange={(e) =>
                                          updateFilterSearchQuery(
                                            column.id,
                                            e.target.value,
                                          )
                                        }
                                        className="block w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-primary-50/50 text-text-dark placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                                      />
                                    </div>
                                  </div>

                                  {/* Filter options */}
                                  <div className="space-y-0.5 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                                    {getFilteredOptions(
                                      column.id,
                                      filterOptions[column.id],
                                    ).map((option) => (
                                      <label
                                        key={option}
                                        className="group flex items-center p-3 hover:bg-primary-50 rounded-lg cursor-pointer transition-all duration-200 border border-transparent hover:border-primary-200/50"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={
                                            columnFilters[column.id]?.includes(
                                              option,
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleColumnFilter(
                                              column.id,
                                              option,
                                              e.target.checked,
                                            )
                                          }
                                          className="w-4 h-4 rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                        />
                                        <span className="ml-3 text-sm font-medium text-text-dark group-hover:text-primary-700 transition-colors duration-200">
                                          {option}
                                        </span>
                                      </label>
                                    ))}

                                    {getFilteredOptions(
                                      column.id,
                                      filterOptions[column.id],
                                    ).length === 0 && (
                                      <div className="flex flex-col items-center justify-center py-8">
                                        <svg
                                          className="w-8 h-8 text-gray-400 mb-2"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                          />
                                        </svg>
                                        <p className="text-sm text-gray-500 text-center font-medium">
                                          {t("noOptionsFound")}
                                        </p>
                                        <p className="text-xs text-gray-400 text-center mt-1">
                                          {t("tryAdjustingSearch")}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {sortedData.map((item, index) => (
                <tr
                  key={index}
                  className={`group transition-colors duration-200 ${
                    index % 2 === 0
                      ? "bg-white hover:bg-primary-50/40"
                      : "bg-gray-50/30 hover:bg-primary-50/40"
                  }`}
                >
                  {visibleColumnObjects.map((column) => {
                    const value = getValueFromItem(item, column.accessor);
                    return (
                      <td
                        key={column.id}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 border-r border-gray-100 last:border-r-0 group-hover:border-primary-200 transition-colors duration-200"
                        style={{ minWidth: "120px" }}
                      >
                        {column.render ? column.render(value, item) : value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {sortedData.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {searchQuery ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                )}
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchQuery
                  ? t("noSearchResults")
                  : t("noDataFound")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery
                  ? `${t("noItemsMatch")} "${searchQuery}". ${t("tryAdjustingTerms")}`
                  : emptyMessage || t("noDataAvailable")}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
                >
                  {t("clearSearch")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && sortedData.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {/* Pagination Info */}
              <div className="text-sm text-gray-700">
                {t("paginationShowing", "Showing")}{' '}
                <span className="font-semibold">{(pagination.currentPage - 1) * pagination.pageSize + 1}</span>
                {' '}{t("paginationTo", "to")}{' '}
                <span className="font-semibold">{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)}</span>
                {' '}{t("paginationOf", "of")}{' '}
                <span className="font-semibold">{pagination.totalItems}</span>
                {' '}{t("paginationResults", "results")}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                {(() => {
                  const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize);

                  return (
                    <>
                      {/* First Page */}
                      <button
                        onClick={() => pagination.onPageChange(1)}
                        disabled={pagination.currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t("paginationFirstPage", "First Page")}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Previous Page */}
                      <button
                        onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {t("paginationPrevious", "Previous")}
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, Math.max(1, totalPages)) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = pagination.currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => pagination.onPageChange(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                pagination.currentPage === pageNum
                                  ? 'bg-primary text-white'
                                  : 'border border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Page */}
                      <button
                        onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === totalPages}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {t("paginationNext", "Next")}
                      </button>

                      {/* Last Page */}
                      <button
                        onClick={() => pagination.onPageChange(totalPages)}
                        disabled={pagination.currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t("paginationLastPage", "Last Page")}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}